import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from '../utils/api';
import { useAuth } from '../context/AuthContext';
import {
  ArrowTopRightOnSquareIcon,
  ChatBubbleLeftRightIcon,
  BellIcon,
  AtSymbolIcon,
  HashtagIcon,
  UserCircleIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const formatDateTime = (dateString) => {
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '';
  return format(d, 'MMM d, h:mm a');
};

const Inbox = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Tab State
  const [activeTab, setActiveTab] = useState('notifications'); 
  
  // Data States
  const [items, setItems] = useState([]); // All notifications
  const [loading, setLoading] = useState(true);
  
  // Chat States
  const [selectedChatId, setSelectedChatId] = useState('general'); // Default
  const [chatData, setChatData] = useState(null); 
  const [chatMessages, setChatMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // DM States
  const [dmUsers, setDmUsers] = useState([]);
  const [userSearchText, setUserSearchText] = useState('');

  // Mention/Tagging states
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const commentInputRef = useRef(null);
  const chatEndRef = useRef(null);
  const userSearchRef = useRef(null);

  // Unified fetching
  const fetchNotifications = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/notifications');
      const list = Array.isArray(data?.data) ? data.data : (Array.isArray(data) ? data : []);
      setItems([...list].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()));
    } catch (e) {
      console.error('Failed to fetch notifications:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsers = React.useCallback(async () => {
    try {
      const { data } = await axios.get('/api/chat/users');
      setDmUsers(data.data || []);
    } catch (e) {
      console.error('Failed to fetch users:', e);
    }
  }, []);

  // Fetch Chat Data (Slack/Teams style)
  const fetchChatContext = React.useCallback(async (chatId) => {
    if (!chatId) return;
    try {
      if (chatId === 'general') {
        const msgRes = await axios.get('/api/chat/general');
        setChatMessages(msgRes.data?.data || []);
        setChatData({ title: 'Workspace General Chat', code: 'General', isGeneral: true });
      } else if (chatId.startsWith('dm-')) {
        const recipientId = chatId.split('-')[1];
        const msgRes = await axios.get(`/api/chat/direct/${recipientId}`);
        setChatMessages(msgRes.data?.data || []);
        
        // Find recipient info
        const recipient = dmUsers.find(u => u._id === recipientId);
        setChatData({ 
          title: recipient ? recipient.email : 'Direct Message', 
          code: recipient ? recipient.name : 'Unknown', 
          isDM: true,
          _id: recipientId 
        });
      } else {
        const bugRes = await axios.get(`/api/bugs/${chatId}`);
        const bug = bugRes.data?.data;
        setChatData({ ...bug, code: bug.bugId });
        const msgRes = await axios.get(`/api/bugs/${bug._id}/messages`);
        setChatMessages(msgRes.data?.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch chat context:', err);
    } finally {
    }
  }, [dmUsers]);

  const handleNotificationClick = async (n) => {
    const getId = (x) => {
      const br = x.bugId ?? x.bug;
      return (typeof br === 'string') ? br : (br?._id || br?.bugId || '');
    };

    const bugId = getId(n);
    if (!n.read) {
      setItems(prev => prev.map(x => (x._id === n._id ? { ...x, read: true } : x)));
      try { await axios.put(`/api/notifications/${n._id}/read`); } catch (_) {}
    }

    if (n.meta?.isChat || (n.type === 'mention' && n.meta?.messageId)) {
        if (n.meta?.channel === 'general') setSelectedChatId('general');
        else if (n.meta?.channel === 'dm') setSelectedChatId(`dm-${n.meta.senderId}`);
        else setSelectedChatId(bugId);
        setActiveTab('chat');
    } else {
       const code = n.meta?.bugCode || (typeof n.bugId === 'object' ? n.bugId?.bugId : '');
       navigate(`/bugs/${code || bugId}`);
    }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put('/api/notifications/read-all');
      setItems(prev => prev.map(i => ({ ...i, read: true })));
    } finally {
      // Done marking read
    }
  };

  useEffect(() => {
    fetchNotifications();
    fetchUsers();
    const params = new URLSearchParams(location.search);
    const bugId = params.get('bugId');
    const tab = params.get('tab');
    if (bugId) setSelectedChatId(bugId);
    if (tab) setActiveTab(tab);
  }, [fetchNotifications, fetchUsers, location.search]);

  useEffect(() => {
    if (activeTab === 'chat') fetchChatContext(selectedChatId);
  }, [activeTab, selectedChatId, fetchChatContext]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const mentions = useMemo(() => items.filter(n => (n.type || n.actionType) === 'mention'), [items]);
  
  const bugThreads = useMemo(() => {
    const map = new Map();
    items.forEach(n => {
      const bid = (typeof n.bugId === 'string') ? n.bugId : (n.bugId?._id || n.bugId?.bugId || '');
      const code = n.meta?.bugCode || n.bugId?.bugId || '';
      if (bid && code && !map.has(bid)) map.set(bid, { id: bid, code: code, lastUpdate: n.createdAt });
    });
    return Array.from(map.values());
  }, [items]);

  const filteredDmUsers = useMemo(() => {
    if (!userSearchText) return dmUsers;
    return dmUsers.filter(u => u.name.toLowerCase().includes(userSearchText.toLowerCase()));
  }, [dmUsers, userSearchText]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!message.trim() || sending) return;
    setSending(true);
    try {
      let endpoint = '';
      if (selectedChatId === 'general') endpoint = '/api/chat/general';
      else if (selectedChatId.startsWith('dm-')) endpoint = `/api/chat/direct/${selectedChatId.split('-')[1]}`;
      else endpoint = `/api/bugs/${chatData._id}/messages`;

      const { data } = await axios.post(endpoint, { text: message });
      setChatMessages(prev => [...prev, data.data]);
      setMessage('');
    } catch (err) {
      console.error('Failed to send message:', err);
    } finally {
      setSending(false);
    }
  };

  const handleMentionChange = (e) => {
    const val = e.target.value;
    setMessage(val);
    const cursorPosition = e.target.selectionStart;
    const textBeforeCursor = val.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([^@\s]*)$/);

    if (mentionMatch) {
      const searchText = mentionMatch[1];
      setShowSuggestions(true);
      setSelectedIndex(0);
      setFilteredSuggestions(dmUsers.filter(u => u.name.toLowerCase().includes(searchText.toLowerCase())));
    } else {
      setShowSuggestions(false);
    }
  };

  const insertMention = (target) => {
    const cursorPosition = commentInputRef.current.selectionStart;
    const textBeforeCursor = message.substring(0, cursorPosition);
    const textAfterCursor = message.substring(cursorPosition);
    const regex = /@([^@\s]*)$/;
    const newTextBefore = textBeforeCursor.replace(regex, `@[${target.name}] `);
    setMessage(newTextBefore + textAfterCursor);
    setShowSuggestions(false);
    commentInputRef.current.focus();
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 h-[calc(100vh-80px)] flex flex-col gap-4 overflow-hidden">
      {/* HEADER */}
      <div className="flex items-center justify-between border-b border-gray-100 pb-3 h-10 shrink-0">
        <div className="flex items-center gap-2">
          <ChatBubbleLeftRightIcon className="h-5 w-5 text-indigo-600" />
          <h1 className="text-lg font-bold text-gray-900 tracking-tight">Collaboration Center</h1>
          <span className="h-4 w-px bg-gray-200 mx-1" />
          <div className="flex items-center gap-1 text-[11px] font-bold text-gray-400 uppercase tracking-widest italic">
            WORKSPACE / {chatData?.code || 'Chat Only'}
          </div>
        </div>
        <button
          onClick={markAllAsRead}
          className="text-[11px] font-black text-indigo-600 hover:text-indigo-800 uppercase tracking-widest"
        >
          SYNC WORKSPACE
        </button>
      </div>

      {/* TABS (Slack Category style) */}
      <div className="flex items-center gap-1 shrink-0">
        {[
          { id: 'notifications', label: 'All Activity', icon: BellIcon, count: items.filter(i => !i.read).length },
          { id: 'chat', label: 'Chat & DMs', icon: ChatBubbleLeftRightIcon },
          { id: 'mentions', label: 'Mentions', icon: AtSymbolIcon, count: mentions.filter(m => !m.read).length }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-bold transition-all ${activeTab === tab.id ? 'bg-indigo-600 text-white shadow-md' : 'text-gray-500 hover:bg-gray-100'}`}
          >
            <tab.icon className="h-3.5 w-3.5" />
            {tab.label}
            {tab.count > 0 && <span className={`ml-1 px-1.5 py-0.5 text-[9px] rounded-full leading-none ${activeTab === tab.id ? 'bg-white text-indigo-600' : 'bg-indigo-600 text-white'}`}>{tab.count}</span>}
          </button>
        ))}
      </div>

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-5 gap-4 overflow-hidden">
        
        {/* SIDEBAR: (Slack/Teams Private Chat focus) */}
        <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl flex flex-col shadow-sm overflow-hidden min-h-0">
          <div className="p-3 border-b border-gray-50 bg-gray-50/10">
            <div className="text-[10px] font-black text-gray-400 tracking-widest uppercase mb-2">
              DISCUSSIONS
            </div>
            <div className="relative">
              <input
                ref={userSearchRef}
                type="text"
                placeholder="Find people..."
                value={userSearchText}
                onChange={(e) => setUserSearchText(e.target.value)}
                className="w-full bg-white border border-gray-200 rounded-lg py-1.5 pl-8 pr-3 text-xs focus:ring-1 focus:ring-indigo-600 transition-all outline-none"
              />
              <MagnifyingGlassIcon className="absolute left-2.5 top-2 h-4 w-4 text-gray-400" />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto overscroll-contain no-scrollbar">
            {loading ? (
              <div className="p-8 text-center text-[10px] font-black tracking-widest text-gray-300 italic uppercase">Syncing...</div>
            ) : activeTab === 'chat' ? (
              <div className="py-2">
                {/* DIRECT MESSAGES (New Focus) */}
                <div className="px-3 mb-6">
                  <div className="text-[9px] font-bold text-gray-300 uppercase tracking-tighter mb-2 flex items-center justify-between group">
                    <span>DIRECT MESSAGES</span>
                    <button 
                       onClick={() => userSearchRef.current?.focus()} 
                       className="text-lg text-indigo-400 hover:text-indigo-600 cursor-pointer p-1"
                    >+</button>
                  </div>
                  <div className="space-y-0.5">
                    {filteredDmUsers.length === 0 ? (
                       <div className="px-3 py-4 border border-dashed border-gray-100 rounded-xl text-[10px] text-gray-300 text-center uppercase tracking-widest italic">
                          No users found
                       </div>
                    ) : (
                      filteredDmUsers.map(u => (
                        <button
                          key={u._id}
                          onClick={() => setSelectedChatId(`dm-${u._id}`)}
                          className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs transition-all ${selectedChatId === `dm-${u._id}` ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                        >
                           <div className={`h-2.5 w-2.5 rounded-full ${selectedChatId === `dm-${u._id}` ? 'bg-indigo-600' : 'bg-gray-200'}`} />
                           <span className="truncate">{u.name}</span>
                           <span className="ml-auto text-[8px] text-gray-300 uppercase font-black">{u.role}</span>
                        </button>
                      ))
                    )}
                  </div>
                </div>

                {/* CHANNELS */}
                <div className="px-3 mb-6">
                  <div className="text-[9px] font-bold text-gray-300 uppercase tracking-tighter mb-2 flex items-center">
                    CHANNELS
                  </div>
                  <button
                    onClick={() => setSelectedChatId('general')}
                    className={`w-full flex items-center gap-3 px-3 py-1.5 rounded-lg text-xs transition-all ${selectedChatId === 'general' ? 'bg-indigo-50 text-indigo-700 font-bold' : 'text-gray-600 hover:bg-gray-50'}`}
                  >
                    <HashtagIcon className="h-3.5 w-3.5 text-gray-400" />
                    general
                  </button>
                </div>

                {/* BUG THREADS */}
                <div className="px-3">
                  <div className="text-[9px] font-bold text-gray-300 uppercase tracking-tighter mb-2">
                    RECENT WORK THREADS
                  </div>
                  {bugThreads.map(t => (
                    <button
                      key={t.id}
                      onClick={() => setSelectedChatId(t.id)}
                      className={`w-full flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all group ${selectedChatId === t.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-50'}`}
                    >
                      <HashtagIcon className="h-3.5 w-3.5 shrink-0 text-gray-300" />
                      <span className="truncate uppercase tracking-tighter">{t.code}</span>
                      <span className="ml-auto text-[8px] text-gray-300 font-black uppercase">{format(new Date(t.lastUpdate), 'MMM d')}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* NOTIFICATIONS LIST */
              <div className="divide-y divide-gray-50">
                {(activeTab === 'notifications' ? items : mentions).map(n => {
                  const actor = n.sender?.name || 'User';
                  return (
                    <button
                      key={n._id}
                      onClick={() => handleNotificationClick(n)}
                      className={`w-full text-left p-3.5 flex gap-3 transition-all hover:bg-gray-50 ${!n.read ? 'bg-indigo-50/10' : ''}`}
                    >
                      <div className={`shrink-0 h-8 w-8 rounded-lg flex items-center justify-center text-[10px] font-black ${n.type === 'mention' ? 'bg-indigo-600 text-white shadow-lg' : 'bg-gray-100 text-gray-500'}`}>
                        {actor.slice(0, 1)}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className={`text-[11px] ${!n.read ? 'font-black text-gray-900' : 'font-bold text-gray-600 uppercase tracking-tighter'}`}>{actor}</span>
                          <span className="text-[9px] text-gray-300 italic">{formatDateTime(n.createdAt)}</span>
                        </div>
                        <p className={`text-[12px] leading-tight line-clamp-2 ${!n.read ? 'text-gray-800 font-medium' : 'text-gray-400'}`}>{n.title || n.message}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* CHAT PANEL: Direct Messaging View */}
        <div className="lg:col-span-3 bg-white border border-gray-100 rounded-2xl flex flex-col shadow-sm overflow-hidden min-h-0 relative">
          {!chatData ? (
             <div className="flex-1 flex flex-col items-center justify-center p-12 text-center bg-gray-50/30">
               <UserCircleIcon className="h-12 w-12 text-gray-200 mb-2" />
               <p className="text-[10px] font-black text-gray-200 uppercase tracking-[0.3em]">Select a colleague to chat</p>
             </div>
          ) : (
            <>
              {/* CHAT HEADER: Professional Slack-style */}
              <div className="px-5 py-3 border-b border-gray-50 flex items-center justify-between bg-white/50 backdrop-blur-sm z-10 h-14 shrink-0">
                <div className="flex items-center gap-3">
                  <div className={`h-10 w-10 rounded-xl flex items-center justify-center text-sm font-black text-white shadow-lg ${chatData.isGeneral ? 'bg-indigo-600' : 'bg-gray-900'}`}>
                    {chatData.code.slice(0, 1).toUpperCase()}
                  </div>
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-black tracking-tight text-gray-900 capitalize">{chatData.code}</span>
                      {!chatData.isGeneral && !chatData.isDM && (
                        <span className="text-[9px] font-black bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded-lg border border-indigo-100 uppercase">BUG THREAD</span>
                      )}
                      {chatData.isDM && <span className="h-1.5 w-1.5 rounded-full bg-green-500" title="Online" />}
                    </div>
                    <span className="text-[10px] font-bold text-gray-400 truncate max-w-[250px] italic">{chatData.title}</span>
                  </div>
                </div>
                {!chatData.isGeneral && !chatData.isDM && (
                  <button onClick={() => navigate(`/bugs/${chatData.code}`)} className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-xl transition-all">
                    <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                  </button>
                )}
              </div>

              {/* MESSAGE FEED: Clean continuous flow */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4 no-scrollbar bg-[#FDFDFD]">
                {chatMessages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center opacity-20 bg-gray-50/50 rounded-3xl border-2 border-dashed border-gray-100">
                    <ChatBubbleLeftRightIcon className="h-10 w-10 text-indigo-200" />
                    <p className="text-[9px] uppercase font-black tracking-[0.2em] mt-3">Beginning of conversation with {chatData.code}</p>
                  </div>
                ) : (
                    chatMessages.map((c, i) => {
                    const isMe = c.sender?._id === user._id || c.sender === user._id;
                    const prevMsg = chatMessages[i - 1];
                    const hideUser = prevMsg && (prevMsg.sender?._id || prevMsg.sender) === (c.sender?._id || c.sender);
                    
                    return (
                      <div key={c._id} className={`flex gap-4 px-2 py-0.5 group hover:bg-gray-100/30 -mx-2 transition-colors ${hideUser ? 'mt-[-14px]' : 'mt-4'}`}>
                         {!hideUser ? (
                            <div className={`shrink-0 h-10 w-10 rounded-xl ${isMe ? 'bg-indigo-600' : 'bg-gray-100'} ${isMe ? 'text-white' : 'text-gray-600'} flex items-center justify-center text-xs font-black shadow-sm ring-1 ring-gray-100 uppercase`}>
                               {(c.sender?.name || 'U').slice(0, 1)}
                            </div>
                         ) : (
                            <div className="w-10 text-[8px] text-transparent group-hover:text-gray-300 text-center font-bold pt-2 select-none">{format(new Date(c.createdAt), 'HH:mm')}</div>
                         )}
                         <div className="min-w-0 flex-1">
                            {!hideUser && (
                              <div className="flex items-center gap-2 mb-1">
                                <span className="text-[12px] font-black text-gray-900 uppercase tracking-tight">{c.sender?.name || 'User'}</span>
                                <span className="text-[9px] text-gray-300 font-bold uppercase">{format(new Date(c.createdAt), 'h:mm a')}</span>
                              </div>
                            )}
                            <div className="text-[13px] text-gray-700 leading-relaxed font-medium">
                              {renderMessageContent(c.text)}
                            </div>
                         </div>
                      </div>
                    );
                  })
                )}
                <div ref={chatEndRef} />
              </div>

              {/* INPUT AREA: Mentions included */}
              <div className="px-5 pb-5 pt-2 bg-white relative">
                {showSuggestions && filteredSuggestions.length > 0 && (
                   <div className="absolute left-5 right-5 bottom-full mb-3 bg-white rounded-xl shadow-2xl border border-gray-100 overflow-hidden z-[100]">
                      {filteredSuggestions.map((u, idx) => (
                        <button
                          key={u._id}
                          className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-all ${selectedIndex === idx ? 'bg-indigo-600 text-white' : 'hover:bg-gray-50'}`}
                          onClick={() => insertMention(u)}
                        >
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center text-[10px] font-black ${selectedIndex === idx ? 'bg-white/20' : 'bg-gray-950 text-white'}`}>
                            {u.name.slice(0, 1)}
                          </div>
                          <div>
                            <p className="text-[11px] font-black uppercase tracking-tighter">{u.name}</p>
                            <p className={`text-[8px] font-bold uppercase italic ${selectedIndex === idx ? 'text-indigo-100' : 'text-gray-400'}`}>{u.role}</p>
                          </div>
                        </button>
                      ))}
                   </div>
                )}
                <form onSubmit={handleSendMessage} className="bg-white border-2 border-gray-100 rounded-2xl overflow-hidden focus-within:border-indigo-600 transition-all shadow-sm">
                  <textarea
                    ref={commentInputRef}
                    rows="2"
                    className="w-full bg-transparent border-0 focus:ring-0 text-[13px] py-3 px-4 resize-none placeholder:text-gray-300 placeholder:text-xs font-medium"
                    placeholder={`Message ${chatData.isDM ? chatData.code : '#' + chatData.code.toLowerCase()}...`}
                    value={message}
                    onChange={handleMentionChange}
                    onKeyDown={(e) => { if(e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(e); } }}
                  />
                  <div className="flex items-center justify-between px-3 pb-2.5 bg-gray-50/20">
                    <div className="flex items-center gap-2">
                       <button type="button" className="p-1.5 text-gray-300 hover:text-indigo-600 hover:bg-white rounded-lg transition-all"><AtSymbolIcon className="h-4 w-4" /></button>
                       <span className="text-[8px] text-gray-300 font-black uppercase tracking-widest italic select-none">Enter to send • Shift+Enter for new line</span>
                    </div>
                    <button type="submit" disabled={!message.trim() || sending} className="px-4 py-1.5 bg-indigo-600 text-white text-[11px] font-black rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-all shadow-lg shadow-indigo-600/20 uppercase tracking-widest">
                       SEND
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-thin::-webkit-scrollbar { width: 4px; }
        .scrollbar-thin::-webkit-scrollbar-thumb { background: #E5E7EB; border-radius: 10px; }
      `}</style>
    </div>
  );
};


const renderMessageContent = (text) => {
  if (!text) return '';
  const parts = text.split(/(@\[[^\]]+\]|#[A-Za-z0-9-]+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@[') && part.endsWith(']')) {
      return <span key={i} className="text-indigo-600 font-black bg-indigo-50 px-1 py-0.5 rounded uppercase text-[12px]">@{part.substring(2, part.length - 1)}</span>;
    }
    if (part.startsWith('#')) return <span key={i} className="text-indigo-600 font-black underline cursor-pointer italic">#{part.substring(1)}</span>;
    return part;
  });
};

export default Inbox;
